import * as Dialog from "@radix-ui/react-dialog";
import {
  ReactNode,
  Dispatch,
  useCallback,
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
} from "react";
import { ExternalPaymentContext } from "./ExternalPayment";
import Icon from "../Icon";
import styles from "./dialog.module.scss";

const useBrowserLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

interface DiaglogProps {
  trigger?: ReactNode;
  title?: string | ReactNode;
  description?: string | ReactNode;
  children: ReactNode;
  fillScreen?: boolean;
  open?: boolean;
  onOpenChange?: Dispatch<React.SetStateAction<boolean>>;
  asChild?: boolean;
  preventCloseAutoFocus?: boolean;
}

const DialogModal = ({
  trigger,
  title,
  description,
  children,
  asChild,
  ...props
}: DiaglogProps) => {
  const [paymentActive, setPaymentActive] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const leases = useRef(new Set<symbol>());
  const mounted = useRef(true);
  useBrowserLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const suspendForPayment = useCallback(() => {
    const token = Symbol("payment");
    if (!leases.current.size)
      previousFocus.current = document.activeElement as HTMLElement | null;
    leases.current.add(token);
    setPaymentActive(true);
    return () => {
      if (!leases.current.delete(token)) return;
      if (!leases.current.size && mounted.current) setPaymentActive(false);
    };
  }, []);
  useBrowserLayoutEffect(() => {
    if (!paymentActive) return;
    // Force-mounted Radix content retains its pointer lock even when open=false.
    // Release that lock only while the external provider owns the screen.
    const content = contentRef.current;
    const previous = document.body.style.pointerEvents;
    document.body.style.pointerEvents = "auto";
    return () => {
      document.body.style.pointerEvents = previous;
      if (mounted.current) {
        const target = previousFocus.current;
        if (target?.isConnected && !target.hasAttribute("disabled"))
          target.focus();
        else content?.focus();
      }
    };
  }, [paymentActive]);
  const suspendedStyle = paymentActive
    ? { visibility: "hidden" as const, pointerEvents: "none" as const }
    : undefined;
  return (
    <ExternalPaymentContext.Provider value={suspendForPayment}>
      <Dialog.Root {...props} open={paymentActive ? false : props.open}>
        <Dialog.Trigger asChild={asChild} className={styles["dialog__trigger"]}>
          {trigger}
        </Dialog.Trigger>

        <Dialog.Portal forceMount={paymentActive || undefined}>
          <Dialog.Overlay
            forceMount={paymentActive || undefined}
            style={suspendedStyle}
            className={styles["dialog__overlay"]}
            data-floater-ignore-outside-click="true"
          >
            <Dialog.Content
              ref={contentRef}
              forceMount={paymentActive || undefined}
              style={suspendedStyle}
              aria-hidden={paymentActive || undefined}
              onInteractOutside={(event) => {
                if (paymentActive) event.preventDefault();
              }}
              onEscapeKeyDown={(event) => {
                if (paymentActive) event.preventDefault();
              }}
              className={`${
                props.fillScreen
                  ? styles["dialog__fill"]
                  : styles["dialog__content"]
              }`}
              data-floater-ignore-outside-click="true"
              onCloseAutoFocus={(event) => {
                if (props.preventCloseAutoFocus) {
                  event.preventDefault();
                }
              }}
            >
              <Dialog.Close
                className={`${
                  props.fillScreen
                    ? styles["dialog__close--fill"]
                    : styles["dialog__close"]
                }`}
              >
                <Icon iconName={"close2"} />
              </Dialog.Close>
              {title && <Dialog.Title>{title}</Dialog.Title>}
              {description && <Dialog.Description></Dialog.Description>}
              {children}
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    </ExternalPaymentContext.Provider>
  );
};

export default DialogModal;
